import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useOktaAuth } from "@okta/okta-react";
import buildParams from "../helpers/paramsBuilder";
import GlobalSearchResponse from "../../models/API/responseModels/globalSearchResponse.model";
import axiosInstance from "../../api";

function useGlobalSearch(
  query: string,
  patientDob: string,
  oktaAuth?: any,
  limit: number = 5
): UseQueryResult<GlobalSearchResponse> {
  const authState = useOktaAuth()?.authState;
  const token =
    authState?.accessToken?.accessToken ??
    oktaAuth?.authState?.accessToken?.accessToken;

  return useQuery({
    queryKey: ["globalSearch", { query, patientDob }],
    queryFn: ({ queryKey }) => {
      const [, { query, patientDob }]: any = queryKey;
      const trimmedQuery = (query as string).trim();

      return axiosInstance
        .get(
          `/v1/orders/global-search${buildParams({
            query: trimmedQuery,
            patientDob,
          })}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((res): GlobalSearchResponse => {
          const responseData = res.data;

          // API returns structure: { patients: { data: [], total: number }, orders: { data: [], total: number } }
          // Return it directly since it matches the expected structure
          if (responseData?.patients) {
            return responseData as GlobalSearchResponse;
          }

          // Fallback: if response is in patient search format, transform it
          if (
            responseData?.data !== undefined ||
            responseData?.total !== undefined
          ) {
            return {
              patients: {
                total: responseData.total || 0,
                nextPage: responseData.nextPage,
                data: responseData.data || [],
              },
              orders: {
                total: 0,
                data: [],
              },
            };
          }

          // Default empty response
          return {
            patients: {
              total: 0,
              data: [],
            },
            orders: {
              total: 0,
              data: [],
            },
          };
        })
        .catch((error) => {
          console.error("Global search error:", error);
          throw error;
        });
    },
    retry: false,
    enabled: !!token && (!!query || !!patientDob),
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
  });
}

export default useGlobalSearch;
